import { useEffect, useState } from 'react';
import { INDICES_REPETITION, TYPES_VOIE, composerAdresse } from '@authsemply/shared';
import { referentiels } from '../../lib/referentiels';

/** Les zones de l'adresse structurée, dans l'ordre de saisie. */
export const CLES_ADRESSE = ['addressNumber', 'addressIndice', 'addressStreetType', 'addressStreet', 'addressLieuDit', 'addressComplement'] as const;

/**
 * Types de voie triés par libellé, un seul code par libellé (le plus lisible) —
 * la liste figée, en attendant (ou à défaut de) la table en base (14/10/2026).
 */
const TYPES_TRIES = (() => {
  const parLibelle = new Map<string, { code: string; libelle: string; fantoir?: true }>();
  for (const t of TYPES_VOIE) {
    const deja = parLibelle.get(t.libelle);
    if (!deja || (t.fantoir && !deja.fantoir) || (Boolean(t.fantoir) === Boolean(deja.fantoir) && t.code.length > deja.code.length)) {
      parLibelle.set(t.libelle, t);
    }
  }
  return [...parLibelle.values()].sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
})();

interface Props {
  /** Valeur courante d'une zone (saisie en cours, sinon enregistrée). */
  valeur: (cle: string) => string;
  onChange: (cle: string, valeur: string) => void;
  erreurs?: Record<string, string>;
  /** Marque la voie comme obligatoire (dossier ouvert à SEmplyApp). */
  requis?: boolean;
  disabled?: boolean;
  /** Préfixe des `id` (deux formulaires sur la même page). */
  prefixe?: string;
}

/**
 * L'adresse d'un dossier, en zones (13/10/2026) : celles de la liasse fiscale
 * EDI-TDFC — numéro, indice bis/ter/quater, type de voie, nom de voie,
 * lieu-dit, complément. La ligne d'adresse « 12 bis avenue des Lilas » est
 * recomposée par le serveur ; elle s'affiche ici en aperçu.
 */
export function ChampsAdresse({ valeur, onChange, erreurs = {}, requis, disabled, prefixe = 'adr' }: Props) {
  const id = (cle: string) => `${prefixe}-${cle}`;
  // La table en base fait foi (éditable) ; la liste figée sert de secours.
  const [types, setTypes] = useState<{ code: string; libelle: string }[]>(TYPES_TRIES);
  useEffect(() => {
    let actif = true;
    referentiels
      .typesVoie()
      .then((t) => actif && t.length > 0 && setTypes(t))
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, []);
  const libelles = new Map(types.map((t) => [t.code, t.libelle]));
  const etoile = requis ? <span className="dos-requis" aria-hidden="true"> *</span> : null;
  const apercu = composerAdresse({
    numero: valeur('addressNumber'),
    indice: valeur('addressIndice'),
    typeVoie: valeur('addressStreetType'),
    voie: valeur('addressStreet'),
    lieuDit: valeur('addressLieuDit'),
  }, (code) => libelles.get(code));
  const erreurDe = (cle: string) => (erreurs[cle] ? <p className="dos-erreur">{erreurs[cle]}</p> : null);

  return (
    <fieldset className="field dos-adresse">
      <legend>
        Adresse{etoile}
      </legend>
      <div className="dos-adresse-ligne">
        <div className={`field dos-adresse-num${erreurs.addressNumber ? ' has-erreur' : ''}`}>
          <label htmlFor={id('addressNumber')}>N°</label>
          <input
            id={id('addressNumber')}
            inputMode="numeric"
            maxLength={4}
            value={valeur('addressNumber')}
            disabled={disabled}
            onChange={(e) => onChange('addressNumber', e.target.value.replace(/\D/g, ''))}
          />
        </div>
        <div className="field dos-adresse-indice">
          <label htmlFor={id('addressIndice')}>Indice</label>
          <select
            id={id('addressIndice')}
            value={valeur('addressIndice')}
            disabled={disabled}
            onChange={(e) => onChange('addressIndice', e.target.value)}
          >
            <option value="">—</option>
            {INDICES_REPETITION.map((i) => (
              <option key={i.code} value={i.code}>
                {i.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className={`field dos-adresse-type${erreurs.addressStreetType ? ' has-erreur' : ''}`}>
          <label htmlFor={id('addressStreetType')}>Type de voie</label>
          <select
            id={id('addressStreetType')}
            value={valeur('addressStreetType')}
            disabled={disabled}
            onChange={(e) => onChange('addressStreetType', e.target.value)}
          >
            <option value="">—</option>
            {types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.libelle}
              </option>
            ))}
            {/* Code enregistré absent de la liste (variante d'un même libellé) : gardé visible. */}
            {valeur('addressStreetType') && !libelles.has(valeur('addressStreetType')) ? (
              <option value={valeur('addressStreetType')}>{valeur('addressStreetType')}</option>
            ) : null}
          </select>
          {erreurDe('addressStreetType')}
        </div>
      </div>
      <div className={`field${erreurs.addressStreet ? ' has-erreur' : ''}`}>
        <label htmlFor={id('addressStreet')}>Nom de la voie{etoile}</label>
        <input
          id={id('addressStreet')}
          maxLength={200}
          placeholder="ex. des Lilas"
          value={valeur('addressStreet')}
          disabled={disabled}
          onChange={(e) => onChange('addressStreet', e.target.value)}
        />
        {erreurDe('addressStreet')}
      </div>
      <div className="field">
        <label htmlFor={id('addressComplement')}>Complément (bâtiment, étage, boîte postale)</label>
        <input
          id={id('addressComplement')}
          maxLength={255}
          value={valeur('addressComplement')}
          disabled={disabled}
          onChange={(e) => onChange('addressComplement', e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={id('addressLieuDit')}>Lieu-dit ou hameau</label>
        <input
          id={id('addressLieuDit')}
          maxLength={100}
          value={valeur('addressLieuDit')}
          disabled={disabled}
          onChange={(e) => onChange('addressLieuDit', e.target.value)}
        />
      </div>
      {erreurDe('addressNumber')}
      {apercu ? <p className="dos-aide">Adresse : {apercu}</p> : null}
    </fieldset>
  );
}
